import { FC, Fragment, memo } from 'react';
import { SecondaryButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import { FilterOutlined } from '@JohnLewisPartnership/jl-design-system/components/icons/FilterOutlined';
import classNames from 'classnames';

import Sort from '../../sort/Sort';
import Pagination from '../../pagination/Pagination';

import styles from './Controls.module.scss';
import PrimaryFacet from '../../facets/primary-facet/PrimaryFacet';
import { useControlsData } from './data/useControlsData';
import PlpViewTypeSelector from '../../plp-view-type-selector/PlpViewTypeSelector';
import PlpCardSizeSelector from '../../plp-card-size-selector/PlpCardSizeSelector';

type ControlsProps = {
  current?: number;
  handleFilterDrawerClick?: () => void;
  handlePrimaryFacetClick?: () => void;
  pages?: number;
  primaryFacetDisplayName?: string;
  sortBy?: string;
  inView?: boolean;
};

const Controls: FC<ControlsProps> = ({
  current = 1,
  handleFilterDrawerClick,
  handlePrimaryFacetClick,
  pages = 1,
  primaryFacetDisplayName,
  sortBy = '',
  inView = true,
}) => {
  const activePage = current;
  const { experiences, facetContext, plpViewType } = useControlsData();

  const { isProductCardSizeFlagOn, isTabletEnhancementsFlagOn } = experiences;
  const { isMoreFiltersButton, applied: appliedFilters } = facetContext;

  const { hasPlpViewTypeSelection } = plpViewType;

  const testId = isMoreFiltersButton ? 'more-filters-button' : 'filters-button';
  const filterCombination = appliedFilters?.filterCombination;
  let appliedFiltersCount = 0;

  if (filterCombination) {
    appliedFiltersCount = filterCombination.includes('|')
      ? filterCombination.split('|').length
      : 1;
  }

  return (
    <Fragment>
      <SecondaryButton
        className={classNames(styles['filter-drawer-button'], {
          [styles['filter-drawer-tablet']]: isTabletEnhancementsFlagOn,
        })}
        data-testid={testId}
        onClick={handleFilterDrawerClick}
      >
        <span className={styles.filterIcon}>
          <FilterOutlined title="Apply filters" size="medium" />
        </span>
        Filter
        {appliedFiltersCount > 0 && (
          <Fragment>&nbsp;({appliedFiltersCount})</Fragment>
        )}
      </SecondaryButton>

      {isTabletEnhancementsFlagOn && primaryFacetDisplayName && (
        <PrimaryFacet
          className="tablet"
          handlePrimaryFacetClick={handlePrimaryFacetClick}
          primaryFacetDisplayName={primaryFacetDisplayName}
        />
      )}

      <Sort sort={sortBy} />

      <div
        className={classNames(styles['align-right'], {
          [styles['align-right--viewSelector']]:
            hasPlpViewTypeSelection || isProductCardSizeFlagOn,
        })}
      >
        <Pagination
          current={activePage}
          pages={pages}
          aria-label="Product listing page pagination"
        />

        {hasPlpViewTypeSelection && (
          <PlpViewTypeSelector className="inControls" name="inControls" />
        )}

        {isProductCardSizeFlagOn && <PlpCardSizeSelector inView={inView} />}
      </div>
    </Fragment>
  );
};

export default memo(Controls);
